<template>
  <q-page class="main-wrapper-page single-page" style="background-color: var(--q-accent)">
    <section class="widget-section widget-section--filter flex no-wrap items-center q-mb-lg">
      <q-form ref="form" class="full-width">
        <div class="row no-wrap full-width q-col-gutter-sm">
          <div class="col">
            <q-input
              v-model="filters.id"
              label="Item ID"
              outlined
              dense
              clearable
              :rules="[validateUuid]"
              @update:model-value="filterTable"
              @clear="reloadTable()"
            ></q-input>
          </div>
          <div class="col">
            <q-input
              v-model="filters.registrationId"
              label="Registration ID"
              outlined
              dense
              clearable
              :rules="[validateUuid]"
              @update:model-value="filterTable"
              @clear="reloadTable()"
            ></q-input>
          </div>
          <div class="col">
            <q-select
              v-model="filters.environment"
              @update:model-value="filterTable"
              emit-value
              class="full-width"
              label="Environment"
              clearable
              map-options
              dense
              outlined
              @clear="reloadTable"
              :options="['DEV', 'NONPROD', 'PROD']"
            />
          </div>
          <div class="col">
            <StatusSelect
              v-model="filters.status"
              @update:model-value="filterTable"
              class="full-width"
              @clear="reloadTable"
              :options="['inprogress', 'completed', 'failed', 'removed']"
            />
          </div>
        </div>
        <div class="row no-wrap full-width q-col-gutter-sm q-mb-xs">
          <div class="col flex no-wrap q-col-gutter-sm">
            <q-input
              v-model="filters.digitalService"
              label="Digital Service"
              outlined
              dense
              clearable
              @update:model-value="filterTable"
              @clear="reloadTable()"
              class="full-width"
            ></q-input>
            <q-input
              v-model="filters.infraService"
              label="Infra Service"
              outlined
              dense
              clearable
              @update:model-value="filterTable"
              @clear="reloadTable()"
              class="full-width"
            ></q-input>
          </div>
          <div class="col flex no-wrap q-col-gutter-sm">
            <DatePicker
              v-model="filters.dateFrom"
              @update:model-value="filterTable"
              label="Created after"
              class="full-width"
              @clear="
                () => {
                  filters.dateFrom = undefined;
                  reloadTable();
                }
              "
            />
            <DatePicker
              v-model="filters.dateTo"
              @update:model-value="filterTable"
              label="Created before"
              class="full-width"
              @clear="
                () => {
                  filters.dateTo = undefined;
                  reloadTable();
                }
              "
            />
          </div>
        </div>
      </q-form>
      <q-icon @click="resetFilters" name="engp:engp-ico-restart" class="q-ml-md cursor-pointer" size="20px">
        <q-tooltip>Reset</q-tooltip>
      </q-icon>
    </section>
    <section class="widget-section widget-section--table">
      <q-table
        :columns="cols"
        :rows="rows"
        class="external-dns-table fixed-table"
        ref="envsTable"
        row-key="id"
        flat
        v-model:pagination="pagination"
        :loading="loading"
        binary-state-sort
        :rows-per-page-options="[20, 50, 100]"
        @request="load"
      >
        <template v-slot:no-data>
          <div class="flex full-width column items-center q-py-lg">
            <q-icon name="mdi-cloud-search" class="q-mb-sm" size="40px" color="primary"></q-icon>
            <span class="block" style="font-size: 14px">No items were found.</span>
          </div>
        </template>
        <template v-slot:body-cell-status="props">
          <q-td style="width: 30px" :props="props">
            <StatusCell v-if="!props.row.deleted_at" :status="props.row.status" />
            <q-icon v-else name="close" color="grey" size="15px"></q-icon>
          </q-td>
        </template>
        <template v-slot:body-cell-digitalService="props">
          <q-td :props="props">
            <router-link :to="`/my-portfolio/${props.row.registration?.digitalService?.apmId}`">{{
              props.row.registration?.digitalService?.apmId
            }}</router-link>
          </q-td>
        </template>
        <template v-slot:body-cell-infraService="props">
          <q-td :props="props">
            <router-link :to="`/services/${props.row.registration?.infraService?.id}`">{{
              props.row.registration?.infraService?.apmid
            }}</router-link>
          </q-td>
        </template>
        <template v-slot:body-cell-actions="props">
          <q-td :props="props">
            <q-btn
              flat
              round
              size="8px"
              style="width: 30px; height: 40px"
              class="btn--no-hover"
              :ripple="false"
              icon="engp:engp-ico-registry_outline"
              @click.stop="goToRegistration($event, props.row)"
            >
              <q-tooltip>Go to registration</q-tooltip>
            </q-btn>
            <WorkflowsBtn
              @showWorkflow="showWorkflow($event, props.row)"
              :loading="loading"
              :relatedWorkflowStatuses="relatedWorkflowStatuses.find((i) => i.id === props.row.id)?.statuses || []"
            />
            <CopyBtn :copyData="props.row.id" notification="Registration environment UUID was copied" />
          </q-td>
        </template>
      </q-table>
    </section>
  </q-page>
</template>

<script>
import dayjs from 'dayjs';
import { getRegistrationEnvironments } from './api/api';
import StatusCell from '../registrations/components/registration-network/components/StatusCell.vue';
import StatusSelect from './components/StatusSelect.vue';
import DatePicker from './components/DatePicker.vue';
import WorkflowsBtn from '@/shared/components/WorkflowsBtn.vue';
import { supportCntrlClick } from '@/shared/helpers';
import { getRelatedWorkflowsStatuses } from '../registrations/api/api';
import CopyBtn from '@/shared/components/CopyBtn.vue';

export default {
  name: 'RegistrationEnvironments',
  components: {
    StatusCell,
    StatusSelect,
    DatePicker,
    WorkflowsBtn,
    CopyBtn,
  },
  data() {
    return {
      relatedWorkflowStatuses: [],
      cols: [
        {
          label: 'Status',
          name: 'status',
          field: 'status',
          align: 'left',
        },
        {
          label: 'Environment',
          name: 'value',
          field: 'value',
          align: 'left',
        },
        {
          label: 'Digital Service',
          name: 'digitalService',
          field: 'digitalService',
          align: 'left',
        },
        {
          label: 'Infra Service',
          name: 'infraService',
          field: 'infraService',
          align: 'left',
        },
        {
          label: 'Created at',
          name: 'created_at',
          field: 'created_at',
          align: 'left',
          format: (val) => dayjs(val).format('DD.MM.YYYY HH:mm'),
          sortable: true,
        },
        {
          label: 'Updated at',
          name: 'updated_at',
          field: 'updated_at',
          align: 'left',
          format: (val) => dayjs(val).format('DD.MM.YYYY HH:mm'),
          sortable: true,
        },
        {
          label: '',
          name: 'actions',
          field: 'actions',
          align: 'right',
        },
      ],
      rows: [],
      pagination: {
        sortBy: 'created_at',
        descending: true,
        page: 1,
        rowsPerPage: 20,
      },
      loading: false,
      timeout: undefined,
      filters: {
        id: undefined,
        registrationId: undefined,
        digitalService: undefined,
        infraService: undefined,
        environment: undefined,
        dateFrom: undefined,
        dateTo: undefined,
        status: undefined,
      },
    };
  },
  methods: {
    goToRegistration(event, record) {
      supportCntrlClick(
        event,
        `/my-portfolio/${record.registration?.digitalService?.apmId}/registration/${record.registration.id}`,
      );
    },
    validateUuid(value) {
      const uuidRegex =
        // eslint-disable-next-line
        /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      return value?.length ? uuidRegex.test(value) || 'Must be a valid UUID' : true;
    },
    showWorkflow(event, record) {
      supportCntrlClick(event, `/workflows?itemId=${record.id}`);
    },
    resetFilters() {
      Object.keys(this.filters).forEach((key) => {
        this.filters[key] = undefined;
      });
      this.reloadTable();
    },
    reloadTable() {
      this.$refs.envsTable.requestServerInteraction();
    },
    filterTable() {
      this.$refs.form.validate().then((valid) => {
        if (valid) {
          this.loading = true;
          if (this.timeout) clearTimeout(this.timeout);
          this.timeout = setTimeout(async () => {
            this.reloadTable();
          }, 1500);
        }
      });
    },
    async load(props) {
      this.loading = true;
      try {
        this.relatedWorkflowStatuses = [];
        const limit = props.pagination.rowsPerPage;
        const { sortBy, descending } = props.pagination;
        const skip = props.pagination.page === 1 ? 0 : (props.pagination.page - 1) * props.pagination.rowsPerPage;
        const { id, registrationId, digitalService, infraService, status, environment, dateFrom, dateTo } =
          this.filters;
        const { count, data } = await getRegistrationEnvironments(
          limit,
          skip,
          sortBy,
          descending,
          id,
          registrationId,
          digitalService,
          infraService,
          status,
          environment,
          dateFrom,
          dateTo,
        );
        this.pagination.rowsNumber = count;
        this.rows = data;
        this.pagination.page = props.pagination.page;
        this.pagination.sortBy = sortBy;
        this.pagination.descending = descending;
        this.pagination.rowsPerPage = props.pagination.rowsPerPage;
        this.$router.replace({ query: this.buildQueryFromFilters() });
        const relatedWorkflowsPrmises = this.rows.map((record) => {
          return getRelatedWorkflowsStatuses(record.id).then((response) => {
            this.relatedWorkflowStatuses.push({ id: record.id, statuses: response });
          });
        });
        await Promise.all([...relatedWorkflowsPrmises]);
      } catch (error) {
        this.$notify('negative', 'Error', error.message);
      } finally {
        this.loading = false;
      }
    },
    buildQueryFromFilters() {
      const query = {};
      for (const key in this.filters) {
        if (this.filters[key]) {
          query[key] = this.filters[key];
        }
      }
      query[this.pagination.sortBy] = this.pagination.descending ? 'DESC' : 'ASC';
      return query;
    },
  },
  mounted() {
    const query = this.$route.query;
    const sortFields = ['createdAt', 'updatedAt'];

    for (const field of sortFields) {
      if (query?.[field]) {
        this.pagination.sortBy = field;
        this.pagination.descending = query[field] !== 'ASC';
      }
    }
    Object.keys(this.filters).forEach((key) => {
      this.filters[key] = query[key] || undefined;
    });
    this.reloadTable();
  },
};
</script>

<style lang="scss">
.fixed-table.q-table--loading thead tr.q-table__progress th {
  top: 0 !important;
}
</style>

<style lang="scss" scoped>
.showBanner {
  .fixed-table {
    height: calc(100vh - 360px);
  }
}
.fixed-table {
  height: calc(100vh - 300px);
}
</style>
